import React, { createContext, ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmDialog, { ConfirmDialogProps, ConfirmVariant } from '../components/UI/ConfirmDialog';

export interface ConfirmOptions {
  title?: string;
  message?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmVariant;
}

export interface PromptOptions extends ConfirmOptions {
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  required?: boolean;
  multiline?: boolean;
}

type PendingDialog = Omit<ConfirmDialogProps, 'onConfirm' | 'onCancel'> & {
  id: number;
  resolve: (value: string | boolean | null) => void;
  isPrompt: boolean;
};

interface ConfirmContextType {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  prompt: (options: PromptOptions) => Promise<string | null>;
}

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

const useConfirmContext = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm/usePrompt must be used within a ConfirmProvider');
  }
  return context;
};

/** `if (!(await confirm({ ... }))) return;` — substitui window.confirm. */
export const useConfirm = () => useConfirmContext().confirm;

/** Resolve com o texto digitado, ou `null` se cancelado — substitui window.prompt. */
export const usePrompt = () => useConfirmContext().prompt;

export const ConfirmProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<PendingDialog | null>(null);
  const dialogRef = useRef<PendingDialog | null>(null);
  const nextId = useRef(0);

  const open = useCallback(
    (options: PromptOptions, isPrompt: boolean) =>
      new Promise<string | boolean | null>((resolve) => {
        // Um diálogo novo cancela o anterior, se ainda estiver aberto
        dialogRef.current?.resolve(dialogRef.current.isPrompt ? null : false);
        const next: PendingDialog = {
          id: ++nextId.current,
          title: options.title || t('common.confirmTitle'),
          message: options.message,
          confirmText: options.confirmText || t('common.confirm'),
          cancelText: options.cancelText || t('common.cancel'),
          variant: options.variant,
          input: isPrompt
            ? {
                label: options.label,
                placeholder: options.placeholder,
                defaultValue: options.defaultValue,
                required: options.required,
                multiline: options.multiline
              }
            : undefined,
          isPrompt,
          resolve
        };
        dialogRef.current = next;
        setDialog(next);
      }),
    [t]
  );

  const confirm = useCallback(
    (options: ConfirmOptions) => open(options, false) as Promise<boolean>,
    [open]
  );
  const prompt = useCallback(
    (options: PromptOptions) => open(options, true) as Promise<string | null>,
    [open]
  );

  const close = useCallback((result: string | boolean | null) => {
    dialogRef.current?.resolve(result);
    dialogRef.current = null;
    setDialog(null);
  }, []);

  const handleCancel = useCallback(() => {
    close(dialogRef.current?.isPrompt ? null : false);
  }, [close]);

  const handleConfirm = useCallback(
    (value?: string) => {
      close(dialogRef.current?.isPrompt ? value ?? '' : true);
    },
    [close]
  );

  const { id, resolve, isPrompt, ...dialogProps } = dialog || ({} as PendingDialog);

  return (
    <ConfirmContext.Provider value={{ confirm, prompt }}>
      {children}
      {dialog && <ConfirmDialog key={id} {...dialogProps} onConfirm={handleConfirm} onCancel={handleCancel} />}
    </ConfirmContext.Provider>
  );
};
